import type { LoginResponse, SessionUser } from '@bssb/contracts';
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { authApi } from '@/lib/api/auth';
import { deviceFingerprint, deviceLabel, storeDeviceLabel } from '@/lib/device';
import {
  type AuthStatus,
  bootstrapSession,
  completeOnlineAuth,
  getSessionState,
  hardWipe,
  idleExpired,
  lockSession,
  patchEnvelope,
  readEnvelope,
  resumeUnlockedSession,
  type SessionEnvelope,
  subscribeSessionState,
  touchActivity,
} from '@/lib/session';
import { setAccessToken } from '@/offline/api-client';
import { hasWrapper, initializeDataKey, unlockDataKey } from '@/offline/crypto';
import { requestPersistentStorage } from '@/offline/storage-quota';
import { startAutoSync } from '@/offline/sync-engine';
import { unlockWithBiometric } from './biometric';

/**
 * Konteks sesi PWA lapangan (T-P7-04/05/06).
 *
 * Sumber kebenarannya modul `lib/session`, bukan state React: mesin sinkronisasi
 * dan interceptor jaringan juga harus bisa membacanya, dan keduanya berjalan di
 * luar pohon komponen. React hanya berlangganan lewat `useSyncExternalStore`,
 * sehingga sesi yang dikunci di tengah pengisian form langsung memantulkan layar
 * tanpa ada komponen yang perlu "ingat" memeriksanya.
 */

/** Login dua langkah: kredensial dulu, lalu PIN yang membuka cache lokal. */
export interface PendingLogin {
  session: LoginResponse;
  /** Perangkat ini belum punya kunci lokal — PIN harus dibuat, bukan dimasukkan. */
  needsPinSetup: boolean;
}

interface AuthContextValue {
  status: AuthStatus;
  user: SessionUser | null;
  envelope: SessionEnvelope | null;
  /** Login online langkah 1. */
  beginLogin: (username: string, password: string) => Promise<PendingLogin>;
  /** Login online langkah 2 — membuka atau membuat kunci lokal. */
  finishLogin: (pending: PendingLogin, pin: string) => Promise<SessionUser>;
  /** Buka kunci perangkat yang sudah punya sesi (offline maupun online). */
  unlockWithPin: (pin: string) => Promise<boolean>;
  unlockWithFingerprint: () => Promise<boolean>;
  lock: () => void;
  signOut: () => Promise<void>;
  refreshOnline: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const sessionState = useSyncExternalStore(subscribeSessionState, getSessionState, () =>
    getSessionState(),
  );
  const [ready, setReady] = useState(false);

  useEffect(() => {
    void bootstrapSession().finally(() => setReady(true));
  }, []);

  // Sinkronisasi otomatis hanya berjalan saat sesi hidup: mesinnya sendiri sudah
  // menolak jalan tanpa kunci terbuka, tetapi menyalakannya lebih awal berarti
  // timer 15 menit berdetak sia-sia sepanjang layar kunci terbuka.
  useEffect(() => {
    if (sessionState.status !== 'authenticated') return;
    void requestPersistentStorage();
    return startAutoSync();
  }, [sessionState.status]);

  // Kunci otomatis setelah 12 jam tanpa aktivitas (FR-AUTH-04). Pemeriksaan juga
  // dilakukan saat tab kembali terlihat — timer tidak jalan saat aplikasi dibekukan.
  useEffect(() => {
    if (sessionState.status !== 'authenticated') return;

    const check = async () => {
      const envelope = await readEnvelope();
      if (envelope && idleExpired(envelope)) lockSession();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
      else void touchActivity();
    };

    const interval = setInterval(() => void check(), 60_000);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [sessionState.status]);

  // Aktivitas nyata pengguna; `touchActivity` sendiri sudah membatasi frekuensi tulis.
  useEffect(() => {
    if (sessionState.status !== 'authenticated') return;

    const onActivity = () => void touchActivity();
    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity);

    return () => {
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
    };
  }, [sessionState.status]);

  const beginLogin = useCallback(async (username: string, password: string) => {
    const fingerprint = await deviceFingerprint();
    const label = deviceLabel();
    await storeDeviceLabel(label);

    const session = await authApi.login({
      username,
      password,
      deviceFingerprint: fingerprint,
      deviceLabel: label,
    });

    setAccessToken(session.tokens.accessToken);

    const envelope = await readEnvelope();
    // Cache milik pengguna lain tidak boleh dibuka dengan sesi ini — dan
    // pemiliknya tidak boleh kehilangan datanya diam-diam, jadi dihapus utuh.
    const foreignCache = envelope !== null && envelope.username !== username;
    if (session.wipeRequested || foreignCache) {
      await hardWipe();
      setAccessToken(session.tokens.accessToken);
    }

    return { session, needsPinSetup: !(await hasWrapper('pin')) };
  }, []);

  const finishLogin = useCallback(async (pending: PendingLogin, pin: string) => {
    if (pending.needsPinSetup) {
      await initializeDataKey({ method: 'pin', pin });
    } else if (!(await unlockDataKey({ method: 'pin', pin }))) {
      throw new Error('PIN salah');
    }

    const profile = await authApi.me().catch(() => null);

    await completeOnlineAuth({
      user: pending.session.user,
      accessToken: pending.session.tokens.accessToken,
      offlineGrantExpiresAt: pending.session.tokens.offlineGrantExpiresAt,
      deviceId: pending.session.deviceId ?? null,
      branchName: profile?.branch?.name ?? null,
    });

    return pending.session.user;
  }, []);

  const unlockWithPin = useCallback(async (pin: string) => {
    if (!(await unlockDataKey({ method: 'pin', pin }))) return false;
    return (await resumeUnlockedSession()) !== null;
  }, []);

  const unlockWithFingerprint = useCallback(async () => {
    const output = await unlockWithBiometric();
    if (!output) return false;
    if (!(await unlockDataKey({ method: 'prf', output }))) return false;
    return (await resumeUnlockedSession()) !== null;
  }, []);

  const signOut = useCallback(async () => {
    // Cache dihapus apa pun jawaban server: perangkat yang gagal memberi tahu
    // server tetap tidak boleh menyimpan data nasabah setelah logout (FR-AUTH-09).
    await authApi.logout().catch(() => undefined);
    await hardWipe();
  }, []);

  const refreshOnline = useCallback(async () => {
    if (!navigator.onLine) return;

    const refreshed = await authApi.refresh().catch(() => null);
    if (!refreshed) return;

    if (refreshed.wipeRequested) {
      await hardWipe();
      return;
    }

    setAccessToken(refreshed.tokens.accessToken);
    await patchEnvelope({
      offlineGrantExpiresAt: refreshed.tokens.offlineGrantExpiresAt,
      lastOnlineAuthAt: new Date().toISOString(),
    });
  }, []);

  // Sesi yang baru dibuka lewat PIN belum punya access token — hanya cookie
  // refresh yang tersisa setelah tab ditutup.
  useEffect(() => {
    if (sessionState.status === 'authenticated') void refreshOnline();
  }, [sessionState.status, refreshOnline]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: ready ? sessionState.status : 'loading',
      user: sessionState.user,
      envelope: sessionState.envelope,
      beginLogin,
      finishLogin,
      unlockWithPin,
      unlockWithFingerprint,
      lock: lockSession,
      signOut,
      refreshOnline,
    }),
    [
      ready,
      sessionState,
      beginLogin,
      finishLogin,
      unlockWithPin,
      unlockWithFingerprint,
      signOut,
      refreshOnline,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth harus dipakai di dalam <AuthProvider>');
  return context;
}
